import { Css, Icon, Palette, SelectField, useTestIds } from "@homebound/beam";
import { observer } from "mobx-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { BulkMaterialAssignmentButton } from "./components/BulkMaterialAssignmentDialog";
import { CameraSidebar } from "./components/CameraSidebar";
import { CompletionIndicator } from "./components/CompletionIndicator";
import { LocationSelector } from "./components/LocationSelector";
import { MaterialSelector } from "./components/MaterialSelector";
import { ResizeDivider } from "./components/ResizeDivider";
import { SaveToBlueprint } from "./components/SaveToBlueprint";
import { TexturePanel } from "./components/TexturePanel";
import { InsideMapsConfigProvider } from "./hooks/useInsideMapsConfig";
import {
  useInsideMapsDesignPackageMaterialsLazyQuery,
  useInsideMapsMaterialVariantsLazyQuery,
  useInsideMapsProductOfferingsQuery,
} from "./queries/insideMapsQueries.generated";
import { InsideMapsConfigStore } from "./store/InsideMapsConfigStore";
import { LocationOption, MaterialVariantOption, TliLocationItem } from "./types";

const store = new InsideMapsConfigStore();

export function InsideMapsPage() {
  return (
    <InsideMapsConfigProvider value={store}>
      <InsideMapsPageContent />
    </InsideMapsConfigProvider>
  );
}

interface PofOption {
  id: string;
  name: string;
}

const InsideMapsPageContent = observer(function InsideMapsPageContent() {
  const tid = useTestIds({});
  const { data: pofData, loading: pofLoading } = useInsideMapsProductOfferingsQuery();
  const [fetchMaterialVariants] = useInsideMapsMaterialVariantsLazyQuery();
  const [fetchDpMaterials] = useInsideMapsDesignPackageMaterialsLazyQuery();

  const [tliMaterialVariants, setTliMaterialVariants] = useState<MaterialVariantOption[]>([]);
  const [dpMaterialVariants, setDpMaterialVariants] = useState<MaterialVariantOption[]>([]);
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [tliLocationItems, setTliLocationItems] = useState<TliLocationItem[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [middlePanelCollapsed, setMiddlePanelCollapsed] = useState(false);
  const [leftWidth, setLeftWidth] = useState(260);
  const [middleWidth, setMiddleWidth] = useState(340);
  const [rightWidth, setRightWidth] = useState(400);
  const containerRef = useRef<HTMLDivElement>(null);

  const clamp = useCallback((val: number, min: number, max: number) => Math.min(max, Math.max(min, val)), []);

  const handleLeftResize = useCallback((delta: number) => setLeftWidth((w) => clamp(w + delta, 180, 500)), [clamp]);
  const handleMiddleResize = useCallback((delta: number) => setMiddleWidth((w) => clamp(w + delta, 200, 600)), [clamp]);
  const handleRightResize = useCallback((delta: number) => setRightWidth((w) => clamp(w - delta, 250, 700)), [clamp]);

  const productOfferings = useMemo(
    () =>
      (pofData?.productOfferings?.entities ?? []).map((e) => ({
        id: e.id,
        name: `${e.displayName || e.name}${e.displayCode ? ` (${e.displayCode})` : ""}`,
        planPackageName: e.planPackage?.displayName || e.planPackage?.name,
        interiorDpId: e.interiorDesignPackage?.id,
        interiorDpName: e.interiorDesignPackage?.displayName || e.interiorDesignPackage?.name,
        configJson: e.readyPlanInsideMapsConfig?.insideMapsConfig ?? null,
      })),
    [pofData],
  );

  const pofOptions: PofOption[] = useMemo(
    () => productOfferings.map((p) => ({ id: p.id, name: p.name })),
    [productOfferings],
  );

  const handleSelectPof = useCallback(
    async (pofId: string | undefined) => {
      if (!pofId) {
        store.reset();
        return;
      }

      store.setSelectedProductOfferingId(pofId);
      const pof = productOfferings.find((p) => p.id === pofId);
      if (!pof) return;

      store.loadFromJson(pof.configJson);

      setLoadingData(true);
      try {
        // Fetch all TLI data with pagination
        const mvMap = new Map<string, MaterialVariantOption>();
        const locMap = new Map<string, LocationOption>();
        const tliLocItems: TliLocationItem[] = [];
        let offset = 0;
        const batchSize = 4000;
        let hasMore = true;

        while (hasMore) {
          const { data } = await fetchMaterialVariants({
            variables: { productOfferingId: pofId, first: batchSize, offset },
          });
          const items = data?.takeoffLineItemsPage?.items ?? [];

          for (const item of items) {
            if (item.materialVariant) {
              const mv = item.materialVariant;
              mvMap.set(mv.id, {
                id: mv.id,
                displayName: mv.displayName,
                modelNumber: mv.modelNumber,
                manufacturerUrl: mv.manufacturerUrl,
                imageUrl: mv.featuredImage?.asset?.downloadUrl ?? null,
                brandName: mv.listing?.brand?.name ?? null,
                costCodeNumber: mv.listing?.item?.costCode?.number ?? null,
                costCodeDisplayName: mv.listing?.item?.costCode?.displayName ?? null,
              });
            }
            if (item.location) {
              locMap.set(item.location.id, {
                id: item.location.id,
                name: item.location.name,
                path: item.location.path ?? "",
                roomId: item.location.room?.id ?? "",
                roomName: item.location.room?.name ?? "",
              });
            }
            if (item.materialVariant && item.location) {
              tliLocItems.push({
                locationId: item.location.id,
                locationName: item.location.name,
                locationPath: item.location.path ?? "",
                roomId: item.location.room?.id ?? "",
                roomName: item.location.room?.name ?? "",
                materialVariantId: item.materialVariant.id,
                materialVariantDisplayName: item.materialVariant.displayName,
              });
            }
          }

          hasMore = items.length === batchSize;
          offset += batchSize;
        }

        setTliMaterialVariants(Array.from(mvMap.values()));
        setLocations(Array.from(locMap.values()));
        setTliLocationItems(tliLocItems);

        // Fetch design package materials
        if (pof.interiorDpId) {
          const { data: dpData } = await fetchDpMaterials({ variables: { dpId: pof.interiorDpId } });
          const dpMvs = new Map<string, MaterialVariantOption>();
          for (const group of dpData?.designPackage?.optionGroups ?? []) {
            for (const opt of group.options ?? []) {
              if (opt.materialVariant) {
                const mv = opt.materialVariant;
                dpMvs.set(mv.id, {
                  id: mv.id,
                  displayName: mv.displayName,
                  modelNumber: mv.modelNumber,
                  manufacturerUrl: mv.manufacturerUrl,
                  imageUrl: mv.featuredImage?.asset?.downloadUrl ?? null,
                  brandName: mv.listing?.brand?.name ?? null,
                  costCodeNumber: mv.listing?.item?.costCode?.number ?? null,
                  costCodeDisplayName: mv.listing?.item?.costCode?.displayName ?? null,
                });
              }
            }
          }
          setDpMaterialVariants(Array.from(dpMvs.values()));
        }
      } finally {
        setLoadingData(false);
      }
    },
    [productOfferings, fetchMaterialVariants, fetchDpMaterials],
  );

  const allMaterialVariants = useMemo(
    () => [...tliMaterialVariants, ...dpMaterialVariants].uniqueBy((mv) => mv.id),
    [tliMaterialVariants, dpMaterialVariants],
  );

  const selectedPof = productOfferings.find((p) => p.id === store.selectedProductOfferingId);

  return (
    <div css={Css.h("100vh").df.fdc.bgGray100.$} {...tid.insideMapsPage}>
      {/* Top action bar */}
      <div css={Css.df.aic.jcsb.px3.pyPx(10).bgWhite.bb.bcGray300.$}>
        <div css={Css.df.aic.gap2.$}>
          <BulkMaterialAssignmentButton materialVariants={allMaterialVariants} />
          <SaveToBlueprint />
        </div>
        <div css={Css.df.fdc.aife.$}>
          <div css={Css.xl.add("fontWeight", "600").$}>
            {store.reviewedTextures} / {store.totalTextures}
          </div>
          <div css={Css.xs.gray500.$}>Textures Reviewed</div>
        </div>
      </div>

      {/* Main content - 4 panel layout */}
      <div css={Css.fg1.df.oh.$} ref={containerRef}>
        {/* Panel 1: Left - POF selector + Completion */}
        {leftPanelCollapsed ? (
          <div
            css={
              Css.wPx(40)
                .bgWhite.br.bcGray300.df.aic.jcc.cursorPointer.add("writingMode", "vertical-rl")
                .add("flexShrink", "0").$
            }
            onClick={() => setLeftPanelCollapsed(false)}
          >
            <span css={Css.smSb.gray600.$}>Product Offering</span>
          </div>
        ) : (
          <div css={Css.bgWhite.br.bcGray300.df.fdc.add("flexShrink", "0").$} style={{ width: leftWidth }}>
            <div css={Css.df.aic.jcsb.px2.pyPx(10).bb.bcGray200.$}>
              <span css={Css.smSb.$}>Product Offering</span>
              <span css={Css.cursorPointer.$} onClick={() => setLeftPanelCollapsed(true)}>
                <Icon icon="chevronLeft" inc={2.5} color={Palette.Gray500} />
              </span>
            </div>
            <div css={Css.fg1.oa.p2.$}>
              <SelectField
                label="Select Product Offering"
                labelStyle="hidden"
                value={store.selectedProductOfferingId ?? undefined}
                onSelect={(val) => handleSelectPof(val)}
                options={pofOptions}
                getOptionValue={(o) => o.id}
                getOptionLabel={(o) => o.name}
              />
              {pofLoading && <div css={Css.xs.gray500.mt1.$}>Loading product offerings...</div>}
              {loadingData && <div css={Css.xs.gray500.mt1.$}>Loading materials & locations...</div>}

              {selectedPof && <div css={Css.mt1.xs.green700.$}>Product offering configured</div>}

              {selectedPof?.planPackageName && (
                <div css={Css.mt3.$}>
                  <div css={Css.xs.gray600.mb1.$}>Plan Package (Blueprint)</div>
                  <div css={Css.sm.px2.pyPx(6).bgGray50.br4.bcGray200.ba.$}>{selectedPof.planPackageName}</div>
                  <div css={Css.mt1.xs.green700.$}>Plan package configured for Blueprint</div>
                </div>
              )}

              {selectedPof?.interiorDpName && (
                <div css={Css.mt3.$}>
                  <div css={Css.xs.gray600.mb1.$}>Design Package</div>
                  <div css={Css.sm.px2.pyPx(6).bgGray50.br4.bcGray200.ba.df.aic.gap1.$}>
                    <span css={Css.blue700.$}>{selectedPof.interiorDpName}</span>
                    <Icon icon="x" inc={2} color={Palette.Gray400} />
                  </div>
                  <div css={Css.mt1.xs.green700.$}>1 design package(s) selected</div>
                </div>
              )}

              <div css={Css.mt3.$}>
                <CompletionIndicator />
              </div>
            </div>
          </div>
        )}

        {/* Divider between Panel 1 and Panel 2 */}
        {!leftPanelCollapsed && <ResizeDivider onResize={handleLeftResize} />}

        {/* Panel 2: InsideMaps Structure */}
        {middlePanelCollapsed ? (
          <div
            css={
              Css.wPx(40)
                .bgWhite.br.bcGray300.df.aic.jcc.cursorPointer.add("writingMode", "vertical-rl")
                .add("flexShrink", "0").$
            }
            onClick={() => setMiddlePanelCollapsed(false)}
          >
            <span css={Css.smSb.gray600.$}>InsideMaps Structure</span>
          </div>
        ) : (
          <div css={Css.bgWhite.br.bcGray300.df.fdc.add("flexShrink", "0").$} style={{ width: middleWidth }}>
            <div css={Css.df.aic.jcsb.px2.pyPx(10).bb.bcGray200.$}>
              <span css={Css.smSb.$}>InsideMaps Structure</span>
              <span css={Css.cursorPointer.$} onClick={() => setMiddlePanelCollapsed(true)}>
                <Icon icon="chevronLeft" inc={2.5} color={Palette.Gray500} />
              </span>
            </div>
            <div css={Css.fg1.oa.$}>
              <CameraSidebar locations={locations} loadingLocations={loadingData} />
            </div>
          </div>
        )}

        {/* Divider between Panel 2 and Panel 3 */}
        {!middlePanelCollapsed && <ResizeDivider onResize={handleMiddleResize} />}

        {/* Panel 3: Textures + Location Assignment */}
        <div css={Css.fg1.bgWhite.br.bcGray300.df.fdc.add("minWidth", "0").$}>
          <div css={Css.fg1.oa.p3.$}>
            <TexturePanel
              materialVariants={allMaterialVariants}
              onSelectTexture={(index) => store.selectTexture(index)}
              locationSelector={<LocationSelector locations={locations} tliLocationItems={tliLocationItems} />}
            />
          </div>
        </div>

        {/* Divider between Panel 3 and Panel 4 */}
        <ResizeDivider onResize={handleRightResize} />

        {/* Panel 4: Material Selector */}
        <div css={Css.bgWhite.df.fdc.add("flexShrink", "0").$} style={{ width: rightWidth }}>
          <div css={Css.fg1.oa.p3.$}>
            <MaterialSelector
              tliMaterialVariants={tliMaterialVariants}
              dpMaterialVariants={dpMaterialVariants}
              allMaterialVariants={allMaterialVariants}
            />
          </div>
        </div>
      </div>
    </div>
  );
});
